import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import toast from 'react-hot-toast'
import { isMobileDevice } from '../utils/responsive'

export default function ProtectedRoute({ children, allowedLevels = null, requiresOrgAdmin = false }) {
  
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [leadershipLevel, setLeadershipLevel] = useState(null)
  const [isOrgAdmin, setIsOrgAdmin] = useState(false)
  const [orgId, setOrgId] = useState(null)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      
      if (session) {
        supabase
          .from('users')
          .select('role, leadership_level, org_id')
          .eq('id', session.user.id)
          .single()
          .then(async ({ data, error }) => {
            if (error) {
              console.error('🟡 User data fetch error:', error)
              setUserRole(null)
              setLeadershipLevel(null)
              setOrgId(null)
              setIsOrgAdmin(false)
              setLoading(false)
            } else {
              setUserRole(data?.role || null)
              setLeadershipLevel(data?.leadership_level || null)
              setOrgId(data?.org_id || null)
              
              if (data?.org_id) {
                const { data: adminData } = await supabase
                  .from('organization_admins')
                  .select('id')
                  .eq('user_id', session.user.id)
                  .eq('org_id', data.org_id)
                  .maybeSingle()
                
                setIsOrgAdmin(!!adminData)
              } else {
                setIsOrgAdmin(false)
              }
              
              setLoading(false)
            }
          })
          .catch(err => {
            console.error('🟡 User data fetch failed:', err)
            setUserRole(null)
            setLeadershipLevel(null)
            setOrgId(null)
            setIsOrgAdmin(false)
            setLoading(false)
          })
      } else {
        setLoading(false)
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user ?? null)
      
      if (session) {
        const { data } = await supabase
          .from('users')
          .select('role, leadership_level, org_id')
          .eq('id', session.user.id)
          .single()
        
        setUserRole(data?.role || null)
        setLeadershipLevel(data?.leadership_level || null)
        setOrgId(data?.org_id || null)
        
        if (data?.org_id) {
          const { data: adminData } = await supabase
            .from('organization_admins')
            .select('id')
            .eq('user_id', session.user.id)
            .eq('org_id', data.org_id)
            .maybeSingle()
          
          setIsOrgAdmin(!!adminData)
        } else {
          setIsOrgAdmin(false)
        }
        
        setLoading(false)
      } else {
        setUserRole(null)
        setLeadershipLevel(null)
        setOrgId(null)
        setIsOrgAdmin(false)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }


  if (requiresOrgAdmin) {
    if (!isOrgAdmin) {
      const isMobile = isMobileDevice()
      
      const levelRoutes = {
        'team': isMobile ? '/team-leader/home' : '/desktop/team-leader/home',
        'group': '/group-leader/home',
        'subgroup': '/subgroup-leader/home',
        'member': '/member/home'
      }
      
      const redirectPath = levelRoutes[leadershipLevel] || '/member/home'
      return <Navigate to={redirectPath} replace />
    }
    return children
  }

  if (allowedLevels && allowedLevels.length > 0) {
    
    // Check if user's actual role matches allowedLevels
    const userLevel = leadershipLevel || 'member'
    const userRoleMatches = allowedLevels.includes(userLevel)
    
    if (isOrgAdmin && !allowedLevels.includes('org_admin')) {
      const isMobile = isMobileDevice()
      
      // If user has a leadership role that matches this route, allow it
      if (userRoleMatches) {
        return children
      }
      
      // Mobile admins can access member routes
      if (isMobile && allowedLevels.includes('member')) {
        return children
      }
      
      // Desktop admins without matching role go to admin dashboard
      return <Navigate to="/admin" replace />
    }
    
    
    if (!allowedLevels.includes(userLevel)) {
      const isMobile = isMobileDevice()
      
      const levelRoutes = {
        'team': isMobile ? '/team-leader/home' : '/desktop/team-leader/home',
        'group': '/group-leader/home',
        'subgroup': '/subgroup-leader/home',
        'member': '/member/home'
      }
      
      if (isOrgAdmin) {
        return <Navigate to="/admin" replace />
      }
      
      const redirectPath = levelRoutes[userLevel] || '/member/home'
      return <Navigate to={redirectPath} replace />
    }
  }

  return children
}